export interface Resource<T> {
  load(): Promise<T>;
}

/** Cache immutable shared values, not disposable renderer/session instances. */
export function createResource<T>(load: () => Promise<T>): Resource<T> {
  let pending: Promise<T> | undefined;
  return {
    load() {
      pending ??= Promise.resolve()
        .then(load)
        .catch((error: unknown) => {
          pending = undefined;
          throw error;
        });
      return pending;
    },
  };
}

export function scheduleResourcePreload(
  resources: readonly Resource<unknown>[],
  onError: (error: unknown) => void,
): () => void {
  const queue = [...resources];
  const connection = 'connection' in navigator ? navigator.connection : undefined;
  const connectionEvents = connection instanceof EventTarget ? connection : undefined;
  let frame: number | undefined;
  let idle: number | undefined;
  let task: number | undefined;
  let disposed = false;
  let loading = false;
  let next = 0;

  const canPreload = () =>
    document.visibilityState === 'visible' &&
    navigator.onLine &&
    !(
      typeof connection === 'object' &&
      connection !== null &&
      'saveData' in connection &&
      connection.saveData === true
    );

  const cancelPending = () => {
    if (frame !== undefined) window.cancelAnimationFrame(frame);
    if (idle !== undefined) window.cancelIdleCallback(idle);
    if (task !== undefined) window.clearTimeout(task);
    frame = idle = task = undefined;
  };

  const stopListening = () => {
    document.removeEventListener('visibilitychange', schedule);
    window.removeEventListener('online', schedule);
    window.removeEventListener('offline', schedule);
    connectionEvents?.removeEventListener('change', schedule);
  };

  const start = () => {
    idle = task = undefined;
    if (disposed || loading || next === queue.length || !canPreload()) return;
    const resource = queue[next++];
    loading = true;
    const finish = () => {
      loading = false;
      schedule();
    };
    void Promise.resolve()
      .then(() => resource.load())
      .then(finish, (error: unknown) => {
        onError(error);
        finish();
      });
  };

  function schedule() {
    cancelPending();
    if (disposed) return;
    if (next === queue.length) {
      stopListening();
      return;
    }
    if (loading || !canPreload()) return;
    // The second frame follows a paint of the usable application.
    frame = window.requestAnimationFrame(() => {
      frame = window.requestAnimationFrame(() => {
        frame = undefined;
        if (typeof window.requestIdleCallback === 'function') idle = window.requestIdleCallback(start);
        else task = window.setTimeout(start, 0);
      });
    });
  }

  document.addEventListener('visibilitychange', schedule);
  window.addEventListener('online', schedule);
  window.addEventListener('offline', schedule);
  connectionEvents?.addEventListener('change', schedule);
  schedule();
  return () => {
    disposed = true;
    cancelPending();
    stopListening();
  };
}
